import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Play, Pause, SkipBack, SkipForward, SpotifyLogo, ArrowLeft, MusicNote, Heart, ShareNetwork } from "@phosphor-icons/react";
import { useFavoritos, compartir } from "@/utils/favoritos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const RadioPage = () => {
  const [tracks, setTracks] = useState([]);
  const [socials, setSocials] = useState({});
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [soloFav, setSoloFav] = useState(false);
  const [favs, toggleFav] = useFavoritos("canciones");
  const audioRef = useRef(null);

  useEffect(() => {
    document.title = "Radio — Viento de Gracia";
    axios.get(`${API}/radio/tracks`).then(({ data }) => {
      setTracks(data);
      const pista = new URLSearchParams(window.location.search).get("pista");
      if (pista) {
        const idx = data.findIndex((t) => t.id === pista);
        if (idx >= 0) setCurrent(idx);
      }
    }).catch(() => {});
    axios.get(`${API}/settings`).then(({ data }) => setSocials(data.socials || {})).catch(() => {});
  }, []);

  const compartirPista = async (t) => {
    const r = await compartir({
      titulo: `${t.titulo} — Radio Viento de Gracia`,
      texto: `Escucha "${t.titulo}" de ${t.artista} en la radio de Viento de Gracia`,
      url: `${window.location.origin}/radio?pista=${t.id}`,
    });
    if (r === "copied") toast.success("Enlace de la canción copiado");
  };

  const marcarFav = (t) => {
    const activo = toggleFav(t.id);
    toast.success(activo ? `"${t.titulo}" guardada en favoritas` : "Quitada de favoritas");
  };

  const visibles = soloFav ? tracks.filter((t) => favs.includes(t.id)) : tracks;

  useEffect(() => {
    if (tracks.length && current === null) setCurrent(0);
  }, [tracks, current]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || current === null || !tracks[current]) return;
    audio.src = `${BACKEND_URL}${tracks[current].url}`;
    audio.load();
    if (playing) audio.play().catch(() => setPlaying(false));
  }, [current]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  }, [playing]);

  const onTime = () => {
    const a = audioRef.current;
    if (a) {
      setProgress(a.currentTime);
      setDuration(a.duration || 0);
    }
  };

  const onEnded = () => {
    if (tracks.length > 1) setCurrent((c) => (c + 1) % tracks.length);
    else setPlaying(false);
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const a = audioRef.current;
    if (a && duration) {
      a.currentTime = ratio * duration;
      setProgress(a.currentTime);
    }
  };

  const track = tracks[current];

  return (
    <div className="min-h-screen bg-ink text-paper" data-testid="radio-page">
      <div className="grain-overlay" />
      <audio ref={audioRef} onTimeUpdate={onTime} onEnded={onEnded} data-testid="radio-audio" />

      <header className="flex items-center justify-between border-b border-white/10 px-6 py-5 lg:px-12">
        <a href="/" className="flex items-center gap-3" data-testid="radio-back-home">
          <img src="/logo-mark-oficial.png" alt="Viento de Gracia" className="h-9 w-9 object-contain" />
          <span className="font-serif text-sm uppercase tracking-[0.3em]">Viento de <em className="italic text-gold">Gracia</em></span>
        </a>
        <a href="/" className="flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.25em] text-paper/60 transition-colors hover:text-fire" data-testid="radio-volver">
          <ArrowLeft size={14} weight="light" /> Volver al sitio
        </a>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-16 lg:px-12 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <p className="mb-8 flex items-center gap-4 text-[10px] font-light uppercase tracking-[0.5em] text-fire" data-testid="radio-eyebrow">
            <span className="inline-block h-px w-16 bg-fire" /> Música de la casa · creada con IA
          </p>
          <h1 className="font-serif uppercase leading-[0.85] tracking-tight" data-testid="radio-title">
            <span className="block text-[16vw] lg:text-9xl">Radio</span>
            <span className="block text-[16vw] italic text-gold lg:text-9xl">Viento de Gracia</span>
          </h1>
          <p className="mt-8 max-w-lg text-sm font-light leading-relaxed tracking-wide text-smoke" data-testid="radio-desc">
            Canciones originales de adoración creadas por nuestra congregación con inteligencia artificial.
            Dales play y llénate del Espíritu donde estés.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 border border-white/10 bg-surface"
          data-testid="radio-player"
        >
          {track ? (
            <div className="p-8 lg:p-10">
              <div className="flex flex-wrap items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <button
                    onClick={() => setPlaying((p) => !p)}
                    data-testid="radio-play-btn"
                    aria-label={playing ? "Pausar" : "Reproducir"}
                    className="flex h-16 w-16 items-center justify-center border border-fire bg-fire text-ink transition-colors duration-300 hover:bg-paper"
                  >
                    {playing ? <Pause size={26} weight="fill" /> : <Play size={26} weight="fill" className="ml-1" />}
                  </button>
                  <div>
                    <p className="text-[9px] font-light uppercase tracking-[0.3em] text-smoke">Sonando ahora</p>
                    <p className="mt-1 font-serif text-2xl" data-testid="radio-current-title">{track.titulo}</p>
                    <p className="text-xs font-light text-smoke" data-testid="radio-current-artist">{track.artista}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => marcarFav(track)}
                      data-testid="radio-fav-current"
                      aria-label="Guardar en favoritas"
                      className={`flex h-11 w-11 items-center justify-center border transition-colors duration-300 ${favs.includes(track.id) ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-paper/70 hover:border-fire hover:text-fire"}`}
                    >
                      <Heart size={18} weight={favs.includes(track.id) ? "fill" : "light"} />
                    </button>
                    <button
                      onClick={() => compartirPista(track)}
                      data-testid="radio-share-current"
                      aria-label="Compartir canción"
                      className="flex h-11 w-11 items-center justify-center border border-white/15 text-paper/70 transition-colors duration-300 hover:border-fire hover:text-fire"
                    >
                      <ShareNetwork size={18} weight="light" />
                    </button>
                  </div>
                  {playing && (
                    <div className="ml-2 flex h-6 items-end gap-1" data-testid="radio-eq">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span key={i} className="eq-bar w-1 bg-fire" style={{ height: "100%", animationDelay: `${i * 0.13}s` }} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setCurrent((c) => (c - 1 + tracks.length) % tracks.length)} data-testid="radio-prev" aria-label="Anterior" className="flex h-11 w-11 items-center justify-center border border-white/15 text-paper/70 transition-colors hover:border-paper hover:text-paper">
                    <SkipBack size={18} weight="light" />
                  </button>
                  <button onClick={() => setCurrent((c) => (c + 1) % tracks.length)} data-testid="radio-next" aria-label="Siguiente" className="flex h-11 w-11 items-center justify-center border border-white/15 text-paper/70 transition-colors hover:border-paper hover:text-paper">
                    <SkipForward size={18} weight="light" />
                  </button>
                </div>
              </div>
              <div className="mt-8">
                <div className="h-1 w-full cursor-pointer bg-white/10" onClick={seek} data-testid="radio-progress">
                  <div className="h-full bg-fire transition-[width] duration-200" style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }} />
                </div>
                <div className="mt-2 flex justify-between text-[10px] font-light tracking-[0.2em] text-smoke">
                  <span data-testid="radio-time-current">{fmt(progress)}</span>
                  <span data-testid="radio-time-total">{fmt(duration)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-16 text-center" data-testid="radio-empty">
              <MusicNote size={40} weight="light" className="mx-auto text-fire" />
              <p className="mt-6 font-serif text-3xl italic text-smoke">Estamos componiendo la primera canción</p>
              <p className="mt-3 text-[10px] font-light uppercase tracking-[0.3em] text-smoke/70">Muy pronto sonará aquí la música de la casa</p>
            </div>
          )}
        </motion.div>

        {tracks.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-12"
            data-testid="radio-tracklist"
          >
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <p className="text-[10px] font-light uppercase tracking-[0.4em] text-smoke">Todas las canciones</p>
              {favs.length > 0 && (
                <button
                  onClick={() => setSoloFav((s) => !s)}
                  data-testid="radio-fav-filter"
                  className={`flex items-center gap-1.5 border px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.2em] transition-colors ${soloFav ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}
                >
                  <Heart size={11} weight={soloFav ? "fill" : "light"} /> Mis favoritas ({favs.filter((id) => tracks.some((t) => t.id === id)).length})
                </button>
              )}
            </div>
            <div className="border-t border-white/10">
              {visibles.length === 0 && (
                <p className="py-10 text-center font-serif text-xl italic text-smoke" data-testid="radio-fav-empty">
                  Aún no tienes favoritas — toca el corazón de una canción
                </p>
              )}
              {visibles.map((t) => {
                const i = tracks.indexOf(t);
                return (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setCurrent(i); setPlaying(true); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { setCurrent(i); setPlaying(true); } }}
                  data-testid={`radio-track-${t.id}`}
                  className={`group flex w-full cursor-pointer items-center gap-6 border-b border-white/10 py-5 text-left transition-colors duration-300 hover:bg-surface lg:px-4 ${i === current ? "bg-surface" : ""}`}
                >
                  <span className={`font-serif text-2xl ${i === current ? "text-fire" : "text-smoke"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1">
                    <span className={`block font-serif text-xl transition-colors ${i === current ? "text-fire" : "text-paper group-hover:text-fire"}`}>{t.titulo}</span>
                    <span className="text-xs font-light text-smoke">{t.artista}</span>
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); marcarFav(t); }}
                    data-testid={`radio-fav-${t.id}`}
                    aria-label={`Favorita ${t.titulo}`}
                    className={`flex h-9 w-9 items-center justify-center transition-colors ${favs.includes(t.id) ? "text-fire" : "text-smoke hover:text-fire"}`}
                  >
                    <Heart size={17} weight={favs.includes(t.id) ? "fill" : "light"} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); compartirPista(t); }}
                    data-testid={`radio-share-${t.id}`}
                    aria-label={`Compartir ${t.titulo}`}
                    className="flex h-9 w-9 items-center justify-center text-smoke transition-colors hover:text-fire"
                  >
                    <ShareNetwork size={16} weight="light" />
                  </button>
                  {i === current && playing ? (
                    <span className="flex h-5 items-end gap-0.5">
                      {[0, 1, 2].map((b) => (
                        <span key={b} className="eq-bar w-0.5 bg-fire" style={{ height: "100%", animationDelay: `${b * 0.15}s` }} />
                      ))}
                    </span>
                  ) : (
                    <Play size={16} weight="light" className="text-smoke transition-colors group-hover:text-fire" />
                  )}
                </div>
                );
              })}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-12 flex flex-wrap items-center justify-between gap-6 border border-white/10 bg-surface p-8"
          data-testid="radio-spotify-card"
        >
          <div className="flex items-center gap-5">
            <span className="flex h-14 w-14 items-center justify-center border border-white/15 text-fire">
              <SpotifyLogo size={26} weight="light" />
            </span>
            <div>
              <p className="font-serif text-2xl">Viento de Gracia en Spotify</p>
              <p className="mt-1 text-xs font-light text-smoke">
                {socials.spotify ? "Escucha la colección completa también en Spotify" : "Estamos preparando nuestro canal oficial — muy pronto"}
              </p>
            </div>
          </div>
          {socials.spotify ? (
            <a href={socials.spotify} target="_blank" rel="noopener noreferrer" data-testid="radio-spotify-link" className="bg-paper px-7 py-3 text-[10px] font-medium uppercase tracking-[0.25em] text-ink transition-colors hover:bg-fire hover:text-paper">
              Abrir Spotify
            </a>
          ) : (
            <span className="border border-dashed border-white/20 px-7 py-3 text-[10px] font-light uppercase tracking-[0.25em] text-smoke" data-testid="radio-spotify-soon">
              Próximamente
            </span>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default RadioPage;
